/**
 * client/camera/dvars.ts — engine constants (dvar defaults) used by the camera and the viewmodel.
 *
 * OWNER: VIEWMODEL+CAMERA agent.
 *
 * Source tags:
 *   [T5 file:line]  KisakBlack (Black Ops 1 MP decompile, tools/src/KisakBlack/src). T6 shares the function
 *                   names (OpenBO2 skeleton: bgame/bg_weapons_view.cpp lists the same BG_CalculateWeaponMovement_*
 *                   set) but the T6 values of these bg_* dvars are registered in protected code and are NOT in
 *                   research/data/dvars_t6mp.json -> every T5 value here is an ESTIMATE for T6.
 *   [DVAR]          research/data/dvars_t6mp.json (real T6 MP registrations).
 *   [MV §x]         research/movement-public.md.
 */

// ---- view bob (camera) [T5 bgame/bg_misc.cpp:746-824; MV §1.5] — ESTIMATE for T6 -------------------
/** bg_viewBobAmplitudeStanding (h, v): multiplier on xy speed. */
export const BG_VIEW_BOB_STANDING: readonly [number, number] = [0.007, 0.007];
export const BG_VIEW_BOB_STANDING_ADS: readonly [number, number] = [0.007, 0.007];
export const BG_VIEW_BOB_DUCKED: readonly [number, number] = [0.0075, 0.0075];
export const BG_VIEW_BOB_DUCKED_ADS: readonly [number, number] = [0.0075, 0.0075];
export const BG_VIEW_BOB_PRONE: readonly [number, number] = [0.08, 0.04];
export const BG_VIEW_BOB_SPRINTING: readonly [number, number] = [0.02, 0.014];
export const BG_VIEW_BOB_DTP: readonly [number, number] = [0.002, 0.002];
export const BG_VIEW_BOB_SWIMMING: readonly [number, number] = [3, 2];
/** bg_bobMax. */
export const BG_VIEW_BOB_MAX = 8;

// ---- weapon bob (viewmodel) [T5 bgame/bg_misc.cpp:839-908; MV §1.5] — ESTIMATE for T6 --------------
export const BG_WEAPON_BOB_BASE = 0.16; //       bg_weaponBobAmplitudeBase
export const BG_WEAPON_BOB_STANDING: readonly [number, number] = [0.055, 0.025];
export const BG_WEAPON_BOB_DUCKED: readonly [number, number] = [0.045, 0.025];
export const BG_WEAPON_BOB_PRONE: readonly [number, number] = [0.02, 0.005];
export const BG_WEAPON_BOB_SPRINTING: readonly [number, number] = [0.02, 0.014];
export const BG_WEAPON_BOB_DTP: readonly [number, number] = [0.002, 0.002];
export const BG_WEAPON_BOB_SWIMMING: readonly [number, number] = [4, 4];
export const BG_WEAPON_BOB_ROLL = 1.5; //        bg_weaponBobAmplitudeRoll
export const BG_WEAPON_BOB_MAX = 8; //           bg_weaponBobMax
export const BG_WEAPON_BOB_LAG = 0.25; //        bg_weaponBobLag

// ---- damage view kick [T5 bgame/bg_misc.cpp:572-579, game_mp/g_active_mp.cpp P_DamageFeedback] ------
export const BG_VIEW_KICK_SCALE = 0.2; //        bg_viewKickScale (ESTIMATE for T6)
export const BG_VIEW_KICK_MIN = 0.5; //          bg_viewKickMin
export const BG_VIEW_KICK_MAX = 90; //           bg_viewKickMax
/** Deflect / return times of the damage flinch, ms [T5 bg_weapons_view.cpp BG_CalculateViewMovement_DamageKick]. */
export const DAMAGE_KICK_DEFLECT_MS = 100;
export const DAMAGE_KICK_RETURN_MS = 400;

// ---- step smoothing [T5 cgame_mp/cg_main_mp.cpp:1570-1588, cg_view_mp.cpp CG_SmoothCameraZ] --------
export const CG_VIEW_Z_SMOOTHING_MIN = 1; //     cg_viewZSmoothingMin (inches)
export const CG_VIEW_Z_SMOOTHING_MAX = 16; //    cg_viewZSmoothingMax
export const CG_VIEW_Z_SMOOTHING_TIME_MS = 100; // cg_viewZSmoothingTime 0.1 s

// ---- landing dip [T5 cgame_mp/cg_view_mp.cpp CG_OffsetFirstPersonView; MV §1.5] -------------------
export const LAND_DEFLECT_MS = 150;
export const LAND_RETURN_MS = 300;
/** The gun gets this fraction of the view dip [T5 cgame/cg_weapons.cpp CG_CalculateWeaponMovement_Landing]. */
export const LAND_GUN_SCALE = 0.25;
/** Max view dip [T5 cgame/cg_event.cpp:1918-1925]. */
export const LAND_VIEWDIP_MAX = 24;

// ---- view kick centering [T5 cgame_mp/cg_view_mp.cpp CG_KickAngles] -------------------------------
export const KICK_STEP_MS = 5;
/** Return velocity is damped to 6 % while moving back toward centre. */
export const KICK_RETURN_DAMP = 0.06;
export const KICK_MAX_ANGLE = 10;
/** Centre speed used with no weapon. */
export const KICK_CENTER_SPEED_NOWEAPON = 2400;

// ---- camera shake [T5 cgame/cg_camerashake.cpp] -----------------------------------------------------
export const SHAKE_SLOTS = 4;
export const SHAKE_PERIOD_DIV_MS = 600; //       sx = time / 600
export const SHAKE_FREQ: readonly [number, number, number] = [25.132742, 47.12389, 37.699112];
export const SHAKE_AMP_DEG: readonly [number, number, number] = [18, 16, 10];

// ---- FOV [DVAR cg_fov 65, cg_fovMin 10, cg_fovScale 1; T5 cgame/cg_camera.cpp CG_GetViewFov] -------
export const CG_FOV_MIN = 10;
export const CG_FOV_SCALE = 1;

// ---- death cam [T5 cgame_mp/cg_main_mp.cpp:1236-1250 + cg_view_mp.cpp CG_OffsetThirdPersonView] ----
export const CG_THIRDPERSON_RANGE = 120; //      cg_thirdPersonRange
export const CG_THIRDPERSON_ANGLE = 0; //        cg_thirdPersonAngle
export const CG_THIRDPERSON_FOCUS_DIST = 512; // focus point distance
/** Focus height above the corpse origin (engine uses the j_head bone of the ragdoll + 8). ESTIMATE. */
export const DEATHCAM_FOCUS_HEIGHT = 24;

// ---- weapon idle sway factor rate [T5 bg_weapons_view.cpp BG_ComputeAndApplyWeaponMovement_IdleAngles] -
export const IDLE_FACTOR_RATE = 0.5; //          fLastIdleFactor moves 0.5 per second
